import React, { useMemo, useCallback, useState } from 'react';
import { useRole } from '../../context/roleContext';
import { formatCurrency } from '../../utils/format';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEdit,
  faTrash,
  faEye,
  faCheck,
  faTimes,
  faTags,
  faUser,
  faUserTie,
  faBuilding,
  faEnvelope,
  faComment,
  faHistory,
  // faFileInvoice,
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS, colorHelpers } from '../../utils/colors';
import DataTable from '../common/DataTable';
import ConfirmDialog from '../common/ConfirmDialog';
import AuditLogsModal from '../common/AuditLogsModal';
import { useTranslation } from 'react-i18next';
import { useChannelSettings } from '../../context/ChannelSettingsContext';

/**
 * Format an ISO date string to a short, human-readable date.
 * Returns an em-dash symbol when the value is falsy or invalid.
 * @param {string} dateString
 * @returns {string}
 */
const formatDate = (dateString, t) => {
  if (!dateString) return t('common.symbols.emDash');
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

/**
 * Format an ISO timestamp to date + time (e.g. "8 Aug 2026, 14:30").
 * Returns null when the value is falsy/invalid so callers can render a fallback.
 * @param {string} tsString
 * @returns {string|null}
 */
const formatDateTime = (tsString) => {
  if (!tsString) return null;
  const d = new Date(tsString);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
};

/**
 * Format price with currency symbol and proper formatting.
 * @param {number|string} price
 * @returns {string}
 */
const formatPrice = (price) => formatCurrency(price);

/**
 * Render a status badge with appropriate styling based on status.
 */
const getStatusBadge = (status, t) => {
  const statusConfig = {
    pending: {
      color: UI_COLORS.STATUS.WARNING,
      label: t('inquiries.table.statuses.pending')
    },
    approved: {
      color: UI_COLORS.STATUS.SUCCESS,
      label: t('inquiries.table.statuses.approved')
    },
    confirmed: {
      color: UI_COLORS.STATUS.SUCCESS,
      label: t('inquiries.table.statuses.confirmed')
    },
    rejected: {
      color: UI_COLORS.STATUS.ERROR,
      label: t('inquiries.table.statuses.rejected')
    },
    cancelled: {
      color: UI_COLORS.STATUS.ERROR,
      label: t('inquiries.table.statuses.cancelled')
    },
    completed: {
      color: UI_COLORS.STATUS.SUCCESS,
      label: t('inquiries.table.statuses.completed')
    },
    converted: {
      color: UI_COLORS.STATUS.SUCCESS,
      label: t('inquiries.table.statuses.converted')
    },
  };

  const config = statusConfig[status?.toLowerCase()] || {
    color: UI_COLORS.TEXT.SECONDARY,
    label: status || t('common.symbols.emDash')
  };

  return (
    <span
      className="inline-block px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{
        backgroundColor: colorHelpers.withOpacity(config.color, 0.1),
        color: config.color,
      }}
    >
      {config.label}
    </span>
  );
};

/**
 * Render customer type badge (New/Existing).
 */
const getCustomerTypeBadge = (isNewCustomer, t) => {
  const color = isNewCustomer ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.INFO;
  const label = isNewCustomer
    ? t('inquiriesModule.inquiries.table.customerTypes.new')
    : t('inquiriesModule.inquiries.table.customerTypes.existing');
  const icon = isNewCustomer ? faUser : faBuilding;

  return (
    <span
      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{
        backgroundColor: colorHelpers.withOpacity(color, 0.1),
        color,
      }}
    >
      <FontAwesomeIcon icon={icon} className="text-sm mr-2" style={{ color }} />
      {label}
    </span>
  );
};

/**
 * "Created by" badge. Portal (customer-raised) inquiries stand out in a distinct
 * colour; staff inquiries show the sales exec's name. Uses the BE-provided
 * `created_by_label` so we never special-case null created_by.
 */
const getSourceBadge = (inquiry, t) => {
  const isPortal = (inquiry?.source || '').toLowerCase() === 'portal';
  const label = inquiry?.created_by_label
    || (isPortal ? t('inquiries.table.copy.customer') : (inquiry?.created_by_name || t('common.symbols.emDash')));
  const color = isPortal ? UI_COLORS.STATUS.WARNING : UI_COLORS.TEXT.SECONDARY;
  const icon = isPortal ? faUser : faUserTie;
  const suffix = isPortal && inquiry?.customer_company ? ` · ${inquiry.customer_company}` : '';
  return (
    <span
      className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold whitespace-nowrap"
      style={{ backgroundColor: colorHelpers.withOpacity(color, 0.12), color }}
      title={`${label}${suffix}`}
    >
      <FontAwesomeIcon icon={icon} className="text-xs mr-1.5" style={{ color }} />
      {label}{suffix}
    </span>
  );
};

const InquiriesTable = ({
  inquiries = [],
  loading = false,
  selectedInquiries = [],
  onSelectInquiry,
  onSelectAll,
  onEditInquiry,
  onDeleteInquiry,
  onViewInquiry,
  onConvertInquiry,
  onCloseInquiry,
  onApproveInquiry,
  onRejectInquiry,
  onSendQuoteNotification,
  viewLoading = false,
  statusLoading = new Set(),
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess, checkEditAccess, isAdmin } = useRole();
  const { emailEnabled, whatsappEnabled } = useChannelSettings();
  // Price-change history + the "Price updated by" column are admin/super-admin only.
  const canViewAudit = isAdmin();
  const hasInquiries = Array.isArray(inquiries) && inquiries.length > 0;
  const selectedInquiriesSet = useMemo(() => new Set(selectedInquiries), [selectedInquiries]);
  const allSelected = hasInquiries && selectedInquiries.length === inquiries.length;

  // Action confirmation state: { type: 'approve' | 'reject', id: string } | null
  const [confirmAction, setConfirmAction] = useState(null);
  const [sendQuoteTarget, setSendQuoteTarget] = useState(null);
  const [sendQuoteLoading, setSendQuoteLoading] = useState(false);
  const [pendingChannel, setPendingChannel] = useState('email');
  // { id, label } of the inquiry whose audit trail is open, or null.
  const [auditTarget, setAuditTarget] = useState(null);

  const openSendDialog = useCallback((id, channel) => {
    setPendingChannel(channel);
    setSendQuoteTarget(id);
  }, []);

  const handleConfirmAction = () => {
    if (confirmAction?.type === 'approve') {
      onApproveInquiry?.(confirmAction.id);
    } else if (confirmAction?.type === 'reject') {
      onRejectInquiry?.(confirmAction.id);
    }
    setConfirmAction(null);
  };

  const handleSendQuoteConfirm = async () => {
    if (sendQuoteTarget) {
      setSendQuoteLoading(true);
      try {
        await onSendQuoteNotification?.(sendQuoteTarget, pendingChannel);
      } finally {
        setSendQuoteLoading(false);
        setSendQuoteTarget(null);
        setPendingChannel('email');
      }
    }
  };

  const dialogConfig = useMemo(() => ({
    approve: {
      title: t('inquiriesModule.inquiries.dialogs.approve.title'),
      message: t('inquiriesModule.inquiries.dialogs.approve.message'),
      confirm: t('inquiriesModule.inquiries.dialogs.approve.confirm'),
      cancel: t('inquiriesModule.inquiries.dialogs.approve.cancel'),
    },
    reject: {
      title: t('inquiriesModule.inquiries.dialogs.reject.title'),
      message: t('inquiriesModule.inquiries.dialogs.reject.message'),
      confirm: t('inquiriesModule.inquiries.dialogs.reject.confirm'),
      cancel: t('inquiriesModule.inquiries.dialogs.reject.cancel'),
    }
  }), [t]);

  const hoverColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BACKGROUNDS.SECTION, 0.5),
    []
  );
  const selectedColor = useMemo(
    () => colorHelpers.withOpacity(UI_COLORS.BUTTONS.PRIMARY.background, 0.1),
    []
  );
  const selectionAccent = UI_COLORS.BUTTONS.PRIMARY.background;

  const columns = useMemo(
    () => [
      {
        id: 'selection',
        label: t('inquiries.table.columns.select'),
        width: '60px',
        align: 'center',
        alignItems: 'center',
        justify: 'center',
        sortable: false,
        renderHeader: ({ loading: isLoading }) => (
          <input
            type="checkbox"
            aria-label={t('inquiriesModule.inquiries.table.aria.selectAll')}
            checked={allSelected}
            onChange={() => onSelectAll?.(!allSelected)}
            disabled={!hasInquiries || isLoading}
            className="rounded focus:ring-2"
            style={{ accentColor: selectionAccent }}
          />
        ),
        renderCell: (inquiry) => {
          const ariaLabel = inquiry.customer_name
            ? `${t('inquiries.table.aria.selectInquiry')} ${inquiry.customer_name}`
            : t('inquiries.table.aria.selectInquiry');

          return (
            <input
              type="checkbox"
              aria-label={ariaLabel}
              checked={selectedInquiriesSet.has(inquiry.id)}
              onChange={(event) => onSelectInquiry?.(inquiry.id, event.target.checked)}
              className="rounded focus:ring-2"
              style={{ accentColor: selectionAccent }}
            />
          );
        },
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'customer_name',
        label: t('inquiries.table.columns.customerName'),
        sortable: true,
        sortAccessor: (inquiry) => (inquiry?.customer_name || '').toLowerCase(),
        renderCell: (inquiry) => (
          <div>
            {inquiry?.customer_company && (
              <div className="text-lg font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
                {inquiry.customer_company}
              </div>
            )}
            <div className="text-xs" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
              {inquiry?.customer_name || t('inquiries.table.copy.noCustomer')}
            </div>
          </div>
        ),
        skeleton: (
          <div>
            <div className="w-32 h-4 bg-gray-300 rounded mb-1" />
            <div className="w-24 h-3 bg-gray-300 rounded" />
          </div>
        ),
      },
      {
        id: 'include_gst',
        label: t('inquiries.table.columns.gst'),
        sortable: false,
        align: 'center',
        renderCell: (inquiry) => (
          <FontAwesomeIcon
            icon={inquiry?.include_gst ? faCheck : faTimes}
            className="text-sm"
            style={{ color: inquiry?.include_gst ? UI_COLORS.STATUS.SUCCESS : UI_COLORS.STATUS.ERROR }}
          />
        ),
        skeleton: <div className="w-4 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'total',
        label: t('inquiries.table.columns.total'),
        sortable: true,
        align: 'right',
        sortAccessor: (inquiry) => parseFloat(inquiry?.total || 0),
        renderCell: (inquiry) => (
          <span className="text-sm font-bold" style={{ color: UI_COLORS.TEXT.PRIMARY }}>
            {formatPrice(inquiry?.total)}
          </span>
        ),
        skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'status',
        label: t('inquiries.table.columns.status'),
        sortable: true,
        align: 'center',
        sortAccessor: (inquiry) => (inquiry?.status || '').toLowerCase(),
        renderCell: (inquiry) => getStatusBadge(inquiry?.status, t),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'created_by',
        label: t('inquiries.table.columns.createdBy'),
        sortable: false,
        renderCell: (inquiry) => getSourceBadge(inquiry, t),
        skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      },
      // "Price updated by" — who last changed an item price, and when. Admin/
      // super-admin only (same gate as the price-change audit history).
      ...(canViewAudit ? [{
        id: 'price_updated_by',
        label: t('inquiries.table.columns.priceUpdatedBy'),
        sortable: true,
        sortAccessor: (inquiry) => inquiry?.price_updated_at ? new Date(inquiry.price_updated_at).getTime() : 0,
        renderCell: (inquiry) => {
          const when = formatDateTime(inquiry?.price_updated_at);
          if (!inquiry?.price_updated_by_name || !when) {
            return <span className="text-sm" style={{ color: UI_COLORS.TEXT.MUTED }}>{t('common.symbols.emDash')}</span>;
          }
          return (
            <div className="leading-tight">
              <div className="text-sm font-medium" style={{ color: UI_COLORS.TEXT.PRIMARY }}>{inquiry.price_updated_by_name}</div>
              <div className="text-xs" style={{ color: UI_COLORS.TEXT.MUTED }}>{when}</div>
            </div>
          );
        },
        skeleton: <div className="w-28 h-4 bg-gray-300 rounded" />,
      }] : []),
      {
        id: 'expected_delivery_date',
        label: t('inquiries.table.columns.deliveryDate'),
        sortable: true,
        sortAccessor: (inquiry) => inquiry?.expected_delivery_date ? new Date(inquiry.expected_delivery_date).getTime() : 0,
        renderCell: (inquiry) => (
          <span className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {formatDate(inquiry?.expected_delivery_date, t)}
          </span>
        ),
        skeleton: <div className="w-24 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'is_new_customer',
        label: t('inquiries.table.columns.customerType'),
        sortable: true,
        align: 'center',
        sortAccessor: (inquiry) => inquiry?.is_new_customer ? 1 : 0,
        renderCell: (inquiry) => getCustomerTypeBadge(Boolean(inquiry?.is_new_customer), t),
        skeleton: <div className="w-20 h-4 bg-gray-300 rounded" />,
      },
      {
        id: 'convert_actions',
        label: t('inquiriesModule.inquiries.table.columns.closeApi'),
        align: 'center',
        sortable: false,
        width: '240px',
        renderCell: (inquiry) => (
          <div className="flex items-center justify-center flex-wrap gap-1">
            {!['converted', 'approved', 'rejected'].includes(inquiry.status?.toLowerCase()) && (
              <>
                {emailEnabled && (
                  <button
                    onClick={() => openSendDialog(inquiry.id, 'email')}
                    disabled={statusLoading.has(inquiry.id)}
                    className="px-2 py-1 text-xs rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed font-medium flex items-center gap-1"
                    style={{
                      color: UI_COLORS.STATUS.INFO,
                      backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                      border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.3)}`,
                    }}
                    title="Send quote via Email"
                  >
                    <FontAwesomeIcon icon={faEnvelope} className="text-xs" />
                    Email
                  </button>
                )}
                {whatsappEnabled && (
                  <button
                    onClick={() => openSendDialog(inquiry.id, 'whatsapp')}
                    disabled={statusLoading.has(inquiry.id)}
                    className="px-2 py-1 text-xs rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed font-medium flex items-center gap-1"
                    style={{
                      color: '#ffffff',
                      backgroundColor: '#25D366',
                      border: '1px solid #25D366',
                    }}
                    title="Send quote via WhatsApp"
                  >
                    <FontAwesomeIcon icon={faComment} className="text-xs" />
                    WhatsApp
                  </button>
                )}
              </>
            )}
            {/* Go to Orders button for approved/converted inquiries */}
            {['approved', 'converted'].includes(inquiry.status?.toLowerCase()) ? (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: { page: 'orders' } }))}
                className="px-3 py-1 text-sm rounded-lg transition-all duration-200 hover:scale-105 font-medium"
                style={{
                  color: UI_COLORS.STATUS.SUCCESS,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1),
                  border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.3)}`,
                }}
              >
                Go to Orders
              </button>
            ) : inquiry.status?.toLowerCase() !== 'rejected' ? (
              <>
                <button
                  onClick={() => onConvertInquiry?.(inquiry.id)}
                  disabled={viewLoading || statusLoading.has(inquiry.id)}
                  className="px-3 py-1 text-sm rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed font-medium"
                  style={{
                    color: UI_COLORS.BUTTONS.PRIMARY.text,
                    backgroundColor: UI_COLORS.BUTTONS.PRIMARY.background,
                  }}
                  title={t('inquiriesModule.inquiries.table.actions.buttons.convert')}
                >
                  {t('inquiriesModule.inquiries.table.actions.buttons.convert')}
                </button>
                <button
                  onClick={() => onCloseInquiry?.(inquiry.id)}
                  disabled={statusLoading.has(inquiry.id)}
                  className="px-3 py-1 text-xs rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed font-medium"
                  style={{
                    color: UI_COLORS.STATUS.ERROR,
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                    border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.2)}`
                  }}
                  title={t('inquiriesModule.inquiries.table.actions.buttons.close')}
                >
                  {t('inquiriesModule.inquiries.table.actions.buttons.close')}
                </button>
              </>
            ) : (
              <span
                className="px-3 py-1 rounded-full text-xs font-semibold"
                style={{
                  backgroundColor: inquiry.status?.toLowerCase() === 'rejected'
                    ? colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1)
                    : colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.1),
                  color: inquiry.status?.toLowerCase() === 'rejected'
                    ? UI_COLORS.STATUS.ERROR
                    : UI_COLORS.STATUS.SUCCESS,
                }}
              >
                {inquiry.status?.toLowerCase() === 'rejected' ? 'Closed' : 'Converted'}
              </span>
            )}
          </div>
        ),
        skeleton: (
          <div className="flex space-x-2 justify-center">
            <div className="w-16 h-6 bg-gray-300 rounded" />
            <div className="w-16 h-6 bg-gray-300 rounded" />
          </div>
        )
      },
      {
        id: 'actions',
        label: t('inquiries.table.columns.actions'),
        align: 'center',
        justify: 'center',
        alignItems: 'center',
        sortable: false,
        renderCell: (inquiry) => (
          <div className="flex items-center justify-center space-x-2">
            <button
              onClick={() => onViewInquiry?.(inquiry.id)}
              disabled={viewLoading}
              className="p-2 rounded-lg transition-all duration-200 hover:scale-105 disabled:opacity-50 disabled:scale-100 disabled:cursor-not-allowed"
              style={{
                color: UI_COLORS.TEXT.PRIMARY,
                backgroundColor: colorHelpers.withOpacity(UI_COLORS.NAVIGATION.HOVER, 0.35),
              }}
              onMouseEnter={(event) => {
                if (!viewLoading) {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.NAVIGATION.HOVER,
                    0.5
                  );
                }
              }}
              onMouseLeave={(event) => {
                event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                  UI_COLORS.NAVIGATION.HOVER,
                  0.35
                );
              }}
              title={t('inquiries.table.tooltips.view')}
            >
              <FontAwesomeIcon icon={faEye} className="text-sm" />
            </button>

            {/* Hide edit for rejected, converted, or approved inquiries */}
            {!['rejected', 'converted', 'approved'].includes(inquiry.status?.toLowerCase()) && checkEditAccess(inquiry.created_at || inquiry.date, { isInquiry: true }) && (
              <button
                onClick={() => onEditInquiry?.(inquiry.id)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.WARNING,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.WARNING,
                    0.2
                  );
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.WARNING,
                    0.1
                  );
                }}
                title={t('inquiries.table.tooltips.edit')}
              >
                <FontAwesomeIcon icon={faEdit} className="text-sm" />
              </button>
            )}
            {checkDeleteAccess() && (
              <button
                onClick={() => onDeleteInquiry?.(inquiry.id)}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.ERROR,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.ERROR, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.ERROR,
                    0.2
                  );
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(
                    UI_COLORS.STATUS.ERROR,
                    0.1
                  );
                }}
                title={t('inquiries.table.tooltips.delete')}
              >
                <FontAwesomeIcon icon={faTrash} className="text-sm" />
              </button>
            )}
            {/* Price-change history — admin/super-admin only. */}
            {canViewAudit && (
              <button
                onClick={() => setAuditTarget({ id: inquiry.id, label: inquiry.customer_company || inquiry.customer_name || `#${inquiry.id}` })}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{
                  color: UI_COLORS.STATUS.INFO,
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1),
                }}
                onMouseEnter={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.2);
                }}
                onMouseLeave={(event) => {
                  event.currentTarget.style.backgroundColor = colorHelpers.withOpacity(UI_COLORS.STATUS.INFO, 0.1);
                }}
                title={t('inquiries.table.tooltips.history')}
              >
                <FontAwesomeIcon icon={faHistory} className="text-sm" />
              </button>
            )}
          </div>
        ),
        skeleton: (
          <div className="flex items-center justify-center space-x-2">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="w-8 h-8 bg-gray-300 rounded-lg" />
            ))}
          </div>
        ),
      },
    ],
    [
      allSelected,
      hasInquiries,
      onDeleteInquiry,
      onEditInquiry,
      onSelectAll,
      onSelectInquiry,
      onViewInquiry,
      selectedInquiriesSet,
      selectionAccent,
      viewLoading,
      statusLoading,
      onConvertInquiry,
      onCloseInquiry,
      checkDeleteAccess,
      checkEditAccess,
      canViewAudit,
      emailEnabled,
      whatsappEnabled,
      openSendDialog,
      t,
    ]
  );

  /**
   * Row property builder for table rows.
   * - Applies hover/selected background styles and event handlers.
   */
  const getRowProps = useCallback(
    (inquiry) => {
      const isSelected = selectedInquiriesSet.has(inquiry.id);
      return {
        className: 'hover:bg-opacity-50 transition-colors',
        style: {
          backgroundColor: isSelected ? selectedColor : 'transparent',
          borderLeft: isSelected ? `3px solid ${selectionAccent}` : 'none',
        },
        onMouseEnter: (event) => {
          if (!isSelected) {
            event.currentTarget.style.backgroundColor = hoverColor;
          }
        },
        onMouseLeave: (event) => {
          event.currentTarget.style.backgroundColor = isSelected ? selectedColor : 'transparent';
        },
      };
    },
    [hoverColor, selectedColor, selectedInquiriesSet, selectionAccent]
  );

  const emptyState = useMemo(
    () => ({
      icon: faTags,
      title: t('inquiries.table.emptyState.title'),
      message: t('inquiries.table.emptyState.message'),
    }),
    [t]
  );

  return (
    <>
      <DataTable
        columns={columns}
        data={Array.isArray(inquiries) ? inquiries : []}
        loading={loading}
        keyField="id"
        enablePagination={false}
        defaultSort={{ field: 'updated_at', direction: 'desc' }}
        getRowProps={getRowProps}
        emptyState={emptyState}
      />
      <ConfirmDialog
        isOpen={!!confirmAction}
        title={confirmAction ? dialogConfig[confirmAction.type]?.title : ''}
        message={confirmAction ? dialogConfig[confirmAction.type]?.message : ''}
        confirmLabel={confirmAction ? dialogConfig[confirmAction.type]?.confirm : ''}
        cancelLabel={confirmAction ? dialogConfig[confirmAction.type]?.cancel : ''}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmAction(null)}
        confirmVariant={confirmAction?.type === 'approve' ? 'success' : 'danger'}
      />
      <ConfirmDialog
        isOpen={!!sendQuoteTarget}
        title={`${t('inquiriesModule.inquiries.dialogs.sendQuote.title')} via ${pendingChannel === 'whatsapp' ? 'WhatsApp' : 'Email'}`}
        message={t('inquiriesModule.inquiries.dialogs.sendQuote.message')}
        confirmLabel={t('inquiriesModule.inquiries.dialogs.sendQuote.confirm')}
        loadingLabel={t('inquiriesModule.inquiries.dialogs.sendQuote.loading')}
        cancelLabel={t('inquiriesModule.inquiries.dialogs.sendQuote.cancel')}
        onConfirm={handleSendQuoteConfirm}
        onCancel={() => !sendQuoteLoading && setSendQuoteTarget(null)}
        confirmVariant="primary"
        loading={sendQuoteLoading}
      />
      {canViewAudit && auditTarget && (
        <AuditLogsModal
          isOpen={!!auditTarget}
          onClose={() => setAuditTarget(null)}
          entityType="inquiry"
          entityId={auditTarget.id}
          entityLabel={auditTarget.label}
        />
      )}
    </>
  );
};

export default InquiriesTable;